import React from 'react';
import { UserCircle, Edit2, LogOut, Plus, Cat, Dog, Trash2, Database, Loader2, ChevronRight, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PetProfile } from '../../types';
import type { Subscription } from '../../types/subscription';
import type { User as FirebaseUser } from "firebase/auth";

interface ProfileViewProps {
  pets: PetProfile[];
  currentPetId: string | null;
  setCurrentPetId: (id: string) => void;
  currentUser: FirebaseUser | null;
  oauthUser: any;
  userForm: any;
  userProfile: any;
  isFree: boolean;
  subscription: Subscription | null;
  onUpgrade: () => void;
  onManageSubscription: () => void;
  onCancelSubscription: () => void;
  isCancellingSubscription: boolean;
  handleOpenEditUser: () => void;
  handleLogout: () => void;
  handleOpenEditPet: () => void;
  handleOpenAddPet: () => void;
  handleDeletePet: (id: string) => void;
  handleSeedData: () => void;
  isSaving: boolean;
  showPWAInstall: boolean;
  handlePWAInstall: () => void;
  formatDateFR: (date: string) => string;
  profile: PetProfile | null;
}

export const ProfileView: React.FC<ProfileViewProps> = ({
  pets,
  currentPetId,
  setCurrentPetId,
  currentUser,
  oauthUser,
  userForm,
  userProfile,
  isFree,
  subscription,
  onUpgrade,
  onManageSubscription,
  onCancelSubscription,
  isCancellingSubscription,
  handleOpenEditUser,
  handleLogout,
  handleOpenEditPet,
  handleOpenAddPet,
  handleDeletePet,
  handleSeedData,
  isSaving,
  showPWAInstall,
  handlePWAInstall,
  formatDateFR,
  profile
}) => {
  const { t } = useTranslation();

  const planLabel = (() => {
    const type = subscription?.type;
    if (!type) return '-';
    if (type === 'premium_monthly') return 'Premium (mensuel)';
    if (type === 'premium_yearly') return 'Premium (annuel)';
    if (type === 'guest') return 'Guest';
    if (type === 'dr_victoria') return 'Dr Victoria';
    if (type === 'free') return 'Free';
    return String(type);
  })();

  const statusLabel = (() => {
    const status = subscription?.status;
    if (!status) return '-';
    if (status === 'active') {
      const end = subscription?.endDate ? new Date(subscription.endDate) : null;
      const isScheduledCancellation = Boolean(subscription?.cancelAtPeriodEnd);
      if (isScheduledCancellation && end && !Number.isNaN(end.getTime()) && end.getTime() > Date.now()) {
        return `Résiliation programmée (jusqu’au ${end.toLocaleDateString('fr-FR')})`;
      }
      return 'Actif';
    }
    if (status === 'cancelled') {
      const end = subscription?.endDate ? new Date(subscription.endDate) : null;
      if (end && !Number.isNaN(end.getTime()) && end.getTime() > Date.now()) {
        return `Résilié (jusqu’au ${end.toLocaleDateString('fr-FR')})`;
      }
      return 'Résilié';
    }
    if (status === 'expired') return 'Expiré';
    if (status === 'pending') return 'En attente';
    return String(status);
  })();

  const displayName =
    (userProfile?.firstName || userProfile?.name)
      ? `${userProfile?.firstName || ''} ${userProfile?.name || ''}`.trim()
      : (currentUser?.displayName || '');

  return (
    <div className="space-y-6">
      {isFree && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-5 rounded-3xl shadow-sm border border-indigo-200">
          <div className="flex items-start gap-4">
            <div className="bg-white/15 p-3 rounded-2xl">
              <Crown size={22} className="text-yellow-300" />
            </div>
            <div className="flex-1">
              <h3 className="font-black tracking-tight text-lg">{t('app:profile.upgrade_banner_title')}</h3>
              <p className="text-white/90 text-sm mt-1">{t('app:profile.upgrade_banner_text')}</p>
              <button
                onClick={onUpgrade}
                className="mt-4 bg-white text-indigo-600 px-4 py-2 rounded-2xl font-bold text-sm hover:bg-indigo-50 transition-colors inline-flex items-center gap-2"
              >
                <Crown size={16} />
                {t('app:profile.upgrade_banner_cta')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100">
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">{t('app:profile.my_account')}</h3>
        <div className="flex items-center gap-4">
          <div className="bg-gray-100 w-12 h-12 rounded-full flex items-center justify-center text-gray-500">
            <UserCircle size={28}/>
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="font-bold text-gray-900 truncate">{currentUser?.email || oauthUser?.email}</p>
            <p className="text-xs text-gray-400">
              {displayName}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleOpenEditUser} className="bg-indigo-50 text-indigo-500 p-3 rounded-2xl active:bg-indigo-100 transition-colors">
              <Edit2 size={20}/>
            </button>
            <button onClick={handleLogout} className="bg-red-50 text-red-500 p-3 rounded-2xl active:bg-red-100 transition-colors">
              <LogOut size={20}/>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100">
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Subscription</h3>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 p-3 rounded-2xl">
            <p className="text-[10px] uppercase text-gray-400 font-black">Plan</p>
            <p className="font-bold text-sm text-gray-800">{planLabel}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-2xl">
            <p className="text-[10px] uppercase text-gray-400 font-black">Statut</p>
            <p className="font-bold text-sm text-gray-800">{statusLabel}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-2xl col-span-2">
            <p className="text-[10px] uppercase text-gray-400 font-black">Période</p>
            <p className="font-bold text-sm text-gray-800">
              {subscription?.startDate ? new Date(subscription.startDate).toLocaleDateString('fr-FR') : '-'}
              {'  '}→{'  '}
              {subscription?.endDate ? new Date(subscription.endDate).toLocaleDateString('fr-FR') : '—'}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={onManageSubscription}
            disabled={!subscription || isCancellingSubscription}
            className="flex-1 bg-indigo-50 text-indigo-700 py-3 rounded-2xl font-bold text-sm border border-indigo-100"
          >
            Gérer (Stripe)
          </button>

          <button
            onClick={() => {
              const type = String(subscription?.type ?? '').toLowerCase();
              const isPaid = type.includes('monthly') || type.includes('yearly');
              if (!isPaid) return;
              if (!confirm('Confirmer la résiliation ? Vous garderez les avantages jusqu\'à la fin de la période (selon Stripe).')) {
                return;
              }
              onCancelSubscription();
            }}
            disabled={isCancellingSubscription || !subscription || subscription.type === 'free' || subscription.type === 'guest'}
            className="flex-1 bg-red-50 text-red-700 py-3 rounded-2xl font-bold text-sm border border-red-100 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isCancellingSubscription ? 'Résiliation…' : 'Résilier'}
          </button>
        </div>
      </div>

      {showPWAInstall && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 rounded-3xl shadow-sm border border-indigo-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-bold text-sm mb-1">Installer Anivista</h4>
              <p className="text-indigo-100 text-xs">Accédez à l'application hors ligne</p>
            </div>
            <button onClick={handlePWAInstall} className="bg-white text-indigo-600 px-4 py-2 rounded-2xl font-bold text-sm active:bg-indigo-50 transition-colors">
              Installer
            </button>
          </div>
        </div>
      )}

      {pets.map(pet => {
        const isSelected = pet.id === currentPetId;
        
        if (isSelected) {
          return (
            <div key={pet.id} className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-gray-100 relative overflow-hidden transition-all animate-in fade-in zoom-in-95 duration-300">
              <div className="flex justify-between items-start mb-6 z-10 relative">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('app:pet.profile')}</h3>
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase text-white ${pet.type === 'chat' ? 'bg-orange-400' : 'bg-indigo-400'}`}>
                  {t(`app:pet.${pet.type}`)}
                </span>
              </div>
              
              <div className="flex flex-col items-center z-10 relative">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-gray-50 shadow-lg mb-4 bg-gray-100 flex items-center justify-center">
                  {pet.imageUrl ? (
                    <img src={pet.imageUrl} className="w-full h-full object-cover"/>
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${pet.type === 'chat' ? 'bg-orange-100 text-orange-500' : 'bg-indigo-100 text-indigo-500'}`}>
                      {pet.type === 'chat' ? <Cat size={48}/> : <Dog size={48}/>}
                    </div>
                  )}
                </div>
                <h2 className="text-2xl font-black text-gray-900">{pet.name}</h2>
                <p className="text-sm text-gray-500 font-bold">{pet.breed}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-8 z-10 relative">
                <div className="bg-gray-50 p-3 rounded-2xl">
                  <p className="text-[10px] uppercase text-gray-400 font-black">{t('app:pet.color')}</p>
                  <p className="font-bold text-sm text-gray-800">{pet.color || '-'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-2xl">
                  <p className="text-[10px] uppercase text-gray-400 font-black">{t('app:pet.id_chip')}</p>
                  <p className="font-bold text-sm text-gray-800 truncate">{pet.identificationNumber || '-'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-2xl col-span-2">
                  <p className="text-[10px] uppercase text-gray-400 font-black">{t('app:pet.birthday')}</p>
                  <p className="font-bold text-sm text-gray-800">{formatDateFR(pet.birthDate)}</p>
                </div>
              </div>

              <div className="flex gap-2 mt-6 z-10 relative">
                <button onClick={handleOpenEditPet} className="flex-1 bg-gray-900 text-white py-3 rounded-2xl font-bold text-sm shadow-lg flex items-center justify-center gap-2">
                  <Edit2 size={16}/> {t('common:buttons.edit')}
                </button>
                <button 
                  onClick={() => handleDeletePet(pet.id)}
                  className="bg-red-600 text-white p-3 rounded-2xl font-bold text-sm shadow-lg hover:bg-red-700 transition-colors"
                  title="Supprimer cet animal"
                >
                  <Trash2 size={16} />
                </button>
                {(currentUser?.email === 'gael@poffet.net' || oauthUser?.email === 'gael@poffet.net') && (
                  <button onClick={handleSeedData} className="px-4 bg-indigo-50 text-indigo-600 rounded-2xl font-bold text-sm border border-indigo-100">
                    {isSaving ? <Loader2 size={20} className="animate-spin"/> : <Database size={20}/>}
                  </button>
                )}
              </div>
            </div>
          );
        } else {
          return (
            <button 
              key={pet.id} 
              onClick={() => setCurrentPetId(pet.id)}
              className="w-full bg-white p-4 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4 transition-all hover:bg-gray-50 active:scale-95 text-left group"
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-sm overflow-hidden shrink-0 ${pet.type === 'chat' ? 'bg-orange-500' : 'bg-indigo-600'}`}>
                {pet.imageUrl ? <img src={pet.imageUrl} className="w-full h-full object-cover" /> : (pet.type === 'chat' ? <Cat size={24} /> : <Dog size={24} />)}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg">{pet.name}</h3>
                <p className="text-xs text-gray-400 font-bold uppercase">{pet.breed}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                <ChevronRight size={20}/>
              </div>
            </button>
          );
        }
      })}

      <button onClick={handleOpenAddPet} className="w-full py-4 border-2 border-dashed border-gray-300 rounded-[2rem] text-gray-400 font-bold hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-2">
        <Plus size={20}/> {t('app:pet.add_another_pet')}
      </button>
    </div>
  );
};
